import { useEffect, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  getTechnologyFilter,
  isTechnologyRequired,
  TechnologyDropdown,
  useTechnologyService,
} from 'domain/shared/career';
import { PositionDropdown, usePositionService } from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import { ClearAfterUnmountWrapper, EnumDropdown, TextArea, TextInput, useFormContext } from 'framework/form';
import { generatePositionProfileName } from '../../shared/utils/positionProfileName';

const inputSx = {
  '& .floating-label-input, & .floating-label-baseInput, & .floating-label-dropdown': {
    mb: 0,
  },
};

const fieldWrapperSx = {
  width: 255,
  ...inputSx,
};

const descriptionWrapperSx = {
  width: 800,
  maxWidth: '100%',
  ...inputSx,
};

type NamedDefinitionPart = {
  id: string;
  name: string;
};

const PositionProfileInformationFields = ({ isDefinitionDisabled }: { isDefinitionDisabled: boolean }) => {
  const { data = {}, onChangeCallback } = useFormContext();
  const { findPositionById } = usePositionService();
  const { findById: findTechnologyById } = useTechnologyService();
  const [position, setPosition] = useState<NamedDefinitionPart>({ id: '', name: '' });
  const [technology, setTechnology] = useState<NamedDefinitionPart>({ id: '', name: '' });
  const specializationId = String(data.specializationId || '');
  const competencyLevel = String(data.competencyLevel || '');
  const technologyId = String(data.technologyId || '');
  const requiresTechnology = isTechnologyRequired(specializationId);
  const positionName = position.id === specializationId ? position.name : '';
  const technologyName = technology.id === technologyId ? technology.name : '';
  const definitionKey = [specializationId, requiresTechnology ? technologyId : '', competencyLevel].join('|');

  const generatedName = generatePositionProfileName({
    positionName,
    technology: requiresTechnology ? technologyName : null,
    competencyLevel,
  });
  const previousDefinitionKey = useRef(definitionKey);
  const pendingDefinitionKey = useRef<string | null>(null);

  useEffect(() => {
    if (previousDefinitionKey.current !== definitionKey) {
      previousDefinitionKey.current = definitionKey;
      pendingDefinitionKey.current = definitionKey;
    }
  }, [definitionKey]);

  useEffect(() => {
    let isActive = true;

    if (!specializationId) {
      setPosition({ id: '', name: '' });
      return undefined;
    }

    setPosition({ id: specializationId, name: '' });
    findPositionById(Number(specializationId))
      .then(positionData => {
        if (isActive) setPosition({ id: specializationId, name: positionData.name ?? '' });
      })
      .catch(() => {
        if (isActive) setPosition({ id: specializationId, name: '' });
      });

    return () => {
      isActive = false;
    };
    // Position service is recreated with its API hook. Specialization explicitly controls lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specializationId]);

  useEffect(() => {
    let isActive = true;

    if (!requiresTechnology || !technologyId) {
      setTechnology({ id: '', name: '' });
      return undefined;
    }

    setTechnology({ id: technologyId, name: '' });
    findTechnologyById(technologyId)
      .then(technologyData => {
        if (isActive) setTechnology({ id: technologyId, name: technologyData.name ?? '' });
      })
      .catch(() => {
        if (isActive) setTechnology({ id: technologyId, name: '' });
      });

    return () => {
      isActive = false;
    };
    // Technology service is recreated with its API hook. Selected technology explicitly controls lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresTechnology, technologyId]);

  useEffect(() => {
    const hasRequiredDefinition = positionName && competencyLevel && (!requiresTechnology || technologyName);

    if (!hasRequiredDefinition || pendingDefinitionKey.current !== definitionKey) return;

    pendingDefinitionKey.current = null;
    onChangeCallback?.({ name: generatedName }, true);
  }, [
    competencyLevel,
    definitionKey,
    generatedName,
    onChangeCallback,
    positionName,
    requiresTechnology,
    technologyName,
  ]);

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <Box sx={fieldWrapperSx}>
          <PositionDropdown source="specializationId" label="Position" disabled={isDefinitionDisabled} />
        </Box>
        {requiresTechnology && (
          <Box sx={fieldWrapperSx}>
            <ClearAfterUnmountWrapper>
              <TechnologyDropdown
                source="technologyId"
                label="Technology"
                customReduce={getTechnologyFilter(specializationId)}
                deps={[specializationId]}
                disabled={isDefinitionDisabled}
              />
            </ClearAfterUnmountWrapper>
          </Box>
        )}
        <Box sx={fieldWrapperSx}>
          <EnumDropdown
            source="competencyLevel"
            label="Level"
            options={EMPLOYEE_COMPETENCY_LEVEL}
            disabled={isDefinitionDisabled}
          />
        </Box>
      </Stack>
      <Box sx={fieldWrapperSx}>
        <TextInput source="name" label="Profile name" />
      </Box>
      <Box sx={descriptionWrapperSx}>
        <TextArea source="description" label="Description" rows={3} />
      </Box>
    </Stack>
  );
};

export default PositionProfileInformationFields;
